import { useMemo, useState } from "react";
import { X, UserPlus } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Field } from "@/components/Bits";

const input = "w-full rounded-sm border border-line bg-white px-4 py-2.5 text-sm outline-none transition-colors focus:border-lime";
const blank = {
  name: "", company: "", email: "", phone: "", billing_address: "", project_address: "", notes: "",
};

/**
 * Creates a client without leaving the project form. Never invites, never emails:
 * the portal stays off until it is switched on from the client record.
 */
export default function NewClientDialog({ open, onClose, onCreated, existing = [] }) {
  const [form, setForm] = useState(blank);
  const [kind, setKind] = useState("individual");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const duplicate = useMemo(() => {
    const email = form.email.trim().toLowerCase();
    const name = form.name.trim().toLowerCase();
    const company = form.company.trim().toLowerCase();
    return existing.find((c) => {
      if (email && (c.email || "").toLowerCase() === email) return true;
      if (name && (c.name || "").toLowerCase() === name) return true;
      return Boolean(company) && (c.company || "").toLowerCase() === company;
    });
  }, [form.email, form.name, form.company, existing]);

  if (!open) return null;

  const submit = async (e) => {
    e?.preventDefault();
    const email = form.email.trim();
    if (!form.name.trim()) {
      toast.error("A client or contact name is required.");
      return;
    }
    if (kind === "business" && !form.company.trim()) {
      toast.error("A business name is required for a business client.");
      return;
    }
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
      toast.error("Enter a valid email address.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/clients", {
        ...form,
        email: email || null,
        client_type: kind === "business" ? "Business" : "Individual",
        portal_enabled: false,
      });
      toast.success(`${data.name} added to your clients`);
      onCreated(data);
      setForm(blank);
      onClose();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-ink/40 sm:items-center sm:p-6" data-testid="new-client-dialog">
      {/* A plain container, not a form: this dialog is rendered inside the project form. */}
      <div
        onKeyDown={(e) => { if (e.key === "Enter" && e.target.tagName !== "TEXTAREA") submit(e); }}
        className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-t-sm bg-white p-6 sm:rounded-sm sm:p-8"
      >
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <span className="overline">New client</span>
            <h3 className="mt-2 font-serif text-2xl text-ink">Add a client</h3>
            <p className="mt-1 text-xs text-fog">No invitation or email is sent — the portal stays off.</p>
          </div>
          <button
            type="button"
            data-testid="new-client-close"
            onClick={() => { setForm(blank); onClose(); }}
            className="rounded-sm border border-line p-1.5 text-stone transition-colors hover:border-fog"
            aria-label="Close"
          >
            <X className="h-4 w-4" strokeWidth={1.5} />
          </button>
        </div>

        <div className="mb-6 inline-flex rounded-sm border border-line bg-white">
          {[["individual", "Individual"], ["business", "Business"]].map(([key, label]) => (
            <button
              key={key}
              type="button"
              data-testid={`client-kind-${key}`}
              onClick={() => setKind(key)}
              className={`px-4 py-2 text-sm transition-colors ${kind === key ? "bg-[#EDEFC4] text-ink" : "text-stone"}`}
            >
              {label}
            </button>
          ))}
        </div>

        {duplicate && (
          <div className="mb-6 rounded-sm border border-line bg-bone px-4 py-3 text-sm text-stone" data-testid="client-duplicate-warning">
            <strong className="text-ink">{duplicate.name}</strong> already looks like this client.{" "}
            <button
              type="button"
              data-testid="client-use-existing"
              onClick={() => { onCreated(duplicate); setForm(blank); onClose(); }}
              className="text-lime-dark underline underline-offset-2"
            >
              Use that record instead
            </button>
          </div>
        )}

        <div className="grid gap-6 sm:grid-cols-2">
          <Field label={kind === "business" ? "Contact Name *" : "Client Name *"}>
            <input data-testid="client-name-input" required value={form.name} onChange={set("name")} className={input} />
          </Field>
          <Field label={kind === "business" ? "Business Name *" : "Company (optional)"}>
            <input data-testid="client-company-input" value={form.company} onChange={set("company")} className={input} />
          </Field>
          <Field label="Email (optional)" hint="Only needed later for portal access">
            <input data-testid="client-email-input" type="email" value={form.email} onChange={set("email")} className={input} />
          </Field>
          <Field label="Phone (optional)">
            <input data-testid="client-phone-input" value={form.phone} onChange={set("phone")} className={input} />
          </Field>
          <Field label="Billing Address (optional)" className="sm:col-span-2">
            <input data-testid="client-billing-input" value={form.billing_address} onChange={set("billing_address")} className={input} />
          </Field>
          <Field label="Project Address (optional)" hint="Only if different from billing" className="sm:col-span-2">
            <input data-testid="client-project-address-input" value={form.project_address} onChange={set("project_address")} className={input} />
          </Field>
          <Field label="Notes (optional)" className="sm:col-span-2">
            <textarea data-testid="client-notes-input" rows={3} value={form.notes} onChange={set("notes")} className={input} />
          </Field>
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <button
            type="button"
            data-testid="client-save-btn"
            onClick={submit}
            disabled={busy}
            className="inline-flex items-center gap-2 rounded-sm bg-lime px-5 py-2.5 text-sm font-medium text-ink transition-colors hover:bg-lime-dark disabled:opacity-60"
          >
            <UserPlus className="h-4 w-4" strokeWidth={1.5} /> {busy ? "Saving…" : "Save Client"}
          </button>
          <button
            type="button"
            onClick={() => { setForm(blank); onClose(); }}
            className="rounded-sm border border-line bg-white px-4 py-2.5 text-sm text-ink transition-colors hover:bg-line/50"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
